import { useEffect, useRef, useState } from 'react';
import { EventBus, type GifResult } from '@campus/core';
import { Icon, Menu } from '../ds/index.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { messagingApi, sendGif } from '../../messaging/store.js';
import { MessagingApiError } from '../../messaging/client.js';

/**
 * Recherche de GIF de la saisie (Giphy, par l'API) : tendances à l'ouverture,
 * recherche 350 ms après la dernière frappe, suite chargée en descendant. Un
 * clic envoie le GIF tout de suite, comme un message.
 */

const SEARCH_DELAY_MS = 350;

// Une seule question au serveur par session : la recherche est-elle configurée ?
let enabledPromise: Promise<boolean> | null = null;
function gifsEnabled(): Promise<boolean> {
  enabledPromise ??= messagingApi.gifStatus().then((r) => r.enabled).catch(() => { enabledPromise = null; return false; });
  return enabledPromise;
}

export function useGifsEnabled(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let alive = true;
    void gifsEnabled().then((v) => { if (alive) setEnabled(v); });
    return () => { alive = false; };
  }, []);
  return enabled;
}

function errorLabel(err: unknown): string {
  const code = err instanceof MessagingApiError ? err.code : '';
  if (code === 'RATE_LIMITED') return 'Trop de recherches d’un coup. Attends une minute.';
  if (code === 'GIF_NOT_FOUND') return 'Ce GIF n’est plus disponible. Choisis-en un autre.';
  if (code === 'QUOTA_EXCEEDED') return 'Plus de place pour les fichiers de la messagerie. Préviens un admin.';
  if (code === 'TOO_MANY_FILES') return 'Trop de fichiers envoyés cette heure-ci. Réessaie plus tard.';
  return 'Les GIF ne répondent pas pour l’instant. Réessaie.';
}

/** Recherche de GIF, ouverte depuis le « + » de la saisie (posée au-dessus d'elle). */
export function GifPanel({ conversationId, onSent, onClose }: { conversationId: string; onSent: () => void; onClose: () => void }) {
  const isMobile = useIsMobile();
  return (
    <Menu
      label="GIF" onClose={onClose} sheet={isMobile} className="pl-msg-menu"
      style={isMobile ? { height: '70dvh', display: 'flex', flexDirection: 'column' }
        : { position: 'absolute', left: 16, bottom: 'calc(100% + 4px)', width: 'min(340px, calc(100% - 32px))', height: 420, display: 'flex', flexDirection: 'column' }}
    >
      <GifSearch conversationId={conversationId} onSent={() => { onClose(); onSent(); }} />
    </Menu>
  );
}

function GifSearch({ conversationId, onSent }: { conversationId: string; onSent: () => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GifResult[]>([]);
  const [next, setNext] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [sending, setSending] = useState<string | null>(null);
  /** Compteur de génération : une réponse d'une recherche dépassée ne remplace rien. */
  const generation = useRef(0);
  const loadingMore = useRef(false);

  useEffect(() => {
    const gen = ++generation.current;
    setLoading(true);
    const t = setTimeout(() => {
      messagingApi.searchGifs(query.trim(), 0).then((page) => {
        if (gen !== generation.current) return;
        setResults(page.results);
        setNext(page.next);
        setFailed(false);
      }).catch((err) => {
        if (gen !== generation.current) return;
        setResults([]);
        setNext(null);
        setFailed(true);
        if (err instanceof MessagingApiError && err.code === 'RATE_LIMITED') EventBus.emit('ui:toast', { message: errorLabel(err), tone: 'warning' });
      }).finally(() => { if (gen === generation.current) setLoading(false); });
    }, query ? SEARCH_DELAY_MS : 0);
    return () => clearTimeout(t);
  }, [query]);

  const loadMore = () => {
    if (next === null || loadingMore.current) return;
    const gen = generation.current;
    loadingMore.current = true;
    messagingApi.searchGifs(query.trim(), next).then((page) => {
      if (gen !== generation.current) return;
      setResults((cur) => {
        const seen = new Set(cur.map((r) => r.id));
        return [...cur, ...page.results.filter((r) => !seen.has(r.id))];
      });
      setNext(page.next);
    }).catch(() => { /* on réessaiera en redescendant */ }).finally(() => { loadingMore.current = false; });
  };

  const pick = async (gif: GifResult) => {
    if (sending) return;
    setSending(gif.id);
    try {
      await sendGif(conversationId, gif.id);
      onSent();
    } catch (err) {
      EventBus.emit('ui:toast', { message: errorLabel(err), tone: 'warning' });
      setSending(null);
    }
  };

  // Deux colonnes qui se remplissent tour à tour, chaque GIF à sa hauteur.
  const columns: GifResult[][] = [[], []];
  const heights = [0, 0];
  for (const r of results) {
    const col = heights[0]! <= heights[1]! ? 0 : 1;
    columns[col]!.push(r);
    heights[col]! += r.height / Math.max(1, r.width);
  }

  return (
    <>
      <div style={{ position: 'relative', padding: 4 }}>
        <span aria-hidden="true" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', display: 'flex', color: 'var(--color-text-secondary)', pointerEvents: 'none' }}>
          <Icon name="search" size={16} />
        </span>
        <input
          className="pl-input" type="search" value={query} autoFocus maxLength={50}
          aria-label="Chercher un GIF" placeholder="Chercher un GIF…"
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: '100%', paddingLeft: 34 }}
        />
      </div>
      <div
        onScroll={(e) => { const el = e.currentTarget; if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) loadMore(); }}
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 4 }}
      >
        {loading && results.length === 0 ? (
          <p className="pl-help" style={{ textAlign: 'center', marginTop: 24 }}>Chargement…</p>
        ) : failed ? (
          <p className="pl-help" style={{ textAlign: 'center', marginTop: 24 }}>Les GIF ne répondent pas pour l’instant.</p>
        ) : results.length === 0 ? (
          <p className="pl-help" style={{ textAlign: 'center', marginTop: 24 }}>Aucun GIF pour « {query.trim()} ».</p>
        ) : (
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', opacity: loading ? 0.6 : 1 }}>
            {columns.map((col, i) => (
              <div key={i} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {col.map((r) => (
                  <button
                    key={r.id} type="button" onClick={() => { void pick(r); }} disabled={!!sending}
                    aria-label={`Envoyer le GIF ${r.title || ''}`.trim()} title={r.title || undefined}
                    style={{
                      position: 'relative', padding: 0, border: 0, borderRadius: 'var(--radius-sm)', overflow: 'hidden', display: 'block', width: '100%',
                      aspectRatio: `${r.width} / ${r.height}`, background: 'var(--color-bg-raised)', cursor: sending ? 'default' : 'pointer',
                      opacity: sending && sending !== r.id ? 0.5 : 1,
                    }}
                  >
                    <img src={messagingApi.gifPreviewUrl(r.previewUrl)} alt="" loading="lazy" draggable={false}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    {sending === r.id && (
                      <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-hud)', color: 'var(--color-text-primary)', fontSize: 'var(--text-xs)', fontWeight: 700 }}>
                        Envoi…
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Exigé par les conditions de Giphy. */}
      <p style={{ margin: 0, padding: '6px 4px 2px', textAlign: 'right', fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--color-text-secondary)' }}>
        Powered by GIPHY
      </p>
    </>
  );
}
